import type {
  AlternativesType,
  FieldPermissions,
  FieldValueDiff,
  MdfDiff,
  MdfDiffStrict,
  TreeChoiceNode,
  MetadataDeltaPayload,
  FieldValueDiffWithNull,
  MdfDiffDelta,
  MdfSchemaNoViews
} from './../dto/mdfModels';

import { isButtonFieldVisibleViewType } from './../dto/mdfModels';
import isUndefined from 'lodash/isUndefined';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import mergeWith from 'lodash/mergeWith';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';
import fromPairs from 'lodash/fromPairs';
import keyBy from 'lodash/keyBy';
import isEqual from 'lodash/isEqual';
import { createUuid } from '../util/createUuid';
import type { FieldValue, ItemMetaData, ItemMetaDataDto, TimedMetadataDto, TimedMetadata } from '../dto/fieldValue';
import type { Alternative, MdfFieldEntity } from './fieldDefinition';
import type {
  MdfEntity, Views, LayoutSettings
} from '../dto/mdfModels';
import { FieldType } from './fieldDefinition';
import { ViewType } from '../dto/mdfModels';
import type { DynamoItemType } from '../dto/itemTypes';
import { ItemType } from '../dto/itemTypes';
import { getPropertiesWithDifferentValues, getPropertiesWithDifferentValuesIncludingNull, isValidGUID } from '../util/general';
import moment from 'moment';
import { TemplateParser } from '../util/templateParser';
import { isTreeChoiceAlternatives } from '../dto/optionListDto';
import type { MdfFieldInitializationRule } from '../dto/mdfInitializationRuleTypes';
import { exceptionEssence } from '../util/exception';
import { convertDateTimetoIsoString, isIsoDateTime } from '../date/dateParsing';
import type { ParsedMetadata } from '../models/XMLToMdf';
import { DEFAULT_MDF_ID, FOLDER_DEFAULT_MDF_ID, DETECTED_TEXT_MDF_ID, RESTRICTED_MATERIAL_MDF_ID, SystemDefaultField, TECHNICAL_DATA_FLAVOR } from './mdfDefaults';
import type { Logger } from '../types/logger';
import { safeArrayAccess } from '../util/safeArrayAccess';


export enum SystemConfigField {
  ConformExportFormat = 'config_conformExportFormat',
  KeldaConformId = 'config_conformKeldaId',
  CleanCopyAutoConform = 'config_archiveCopyAutoConform',
  CleanCopyTracksToMute = 'config_archiveCopyTracksToMute',
}

// These fields are not system fields, but may be recognized
// and treated in a special way by some code paths.
export enum SpecialMeaningField {

  // When creating a new auto detected person from an image,
  // if this source image has a metadata field with this fieldId,
  // we will use that as the name of the person instead of the
  // default unknown person name.
  PERSON_NAME_MDF_FIELD_ID = 'personName',


  MIMIR_TRINT_ID_FIELD_ID = 'trint_id',
}

export enum RestrictedMaterialField {
  Comment = 'restricted_material_comment',
  SourceId = 'restricted_material_source_id',
  UsageRule = 'restricted_material_usage_rule',
  IngestTime = 'restricted_material_ingest_time',
}

export enum DetectedTextField {
  Text = 'detected_text_text',
  Top = 'detected_text_top',
  Left = 'detected_text_left',
  Width = 'detected_text_width',
  Height = 'detected_text_height',
}

export type MdfAndSettings = MdfFieldEntity & {
  settings?: LayoutSettings;
};

// Size limit for file type mdf field.
export const MAX_FILE_TYPE_FIELD_SIZE_IN_BYTES = 2000000;

const typesWithDefaultMdfSupport: ReadonlySet<DynamoItemType> = new Set([ItemType.VIDEO, ItemType.IMAGE, ItemType.AUDIO, ItemType.FILE]);

export const supportsDefaultMdfType = (itemType: DynamoItemType): boolean => {
  return typesWithDefaultMdfSupport.has(itemType as any);
};

/**
 * Fetch a system default field.
 *
 * Note: this function is returning any so check for validity.
 *
 * @param item The item to fetch the field for.
 * @param field The SystemDefaultField to fetch
 * @param highlighted Whether the fetch the highlights field or regular field. Optional.
 */
export const getItemSystemDefaultField = (item: { metadata?: Partial<ItemMetaDataDto> }, field: SystemDefaultField, highlighted = false): any => {
  if (!item || !item.metadata) {
    return undefined;
  }

  if (highlighted && item.metadata.highlights) {
    return item.metadata.highlights[`${field}Highlighted`]; // TO-DO: Fix reactivity / sync issues
  } else if (!highlighted && item.metadata.formData) {
    return item.metadata.formData[field];
  }
  return undefined;
};

export const supportsValidationRules = (field: MdfFieldEntity): boolean => {
  return (field.type === FieldType.Text) || !!field.freeform || (field.type === FieldType.File);
};


export const getIdFromFieldId = (fieldId: string, schema: MdfEntity): string | null => {
  if (!fieldId || !schema.fields) {
    return null;
  }

  for (const field of schema.fields) {
    if (field.fieldId === fieldId) {
      return field.id;
    }
  }
  return null;
};

export const validateChoiceValue = (field: MdfFieldEntity, alternatives: Alternative[] | undefined, value: FieldValue): boolean => {
  if (field.type !== FieldType.Choice) {
    return false;
  }
  if (!alternatives) {
    return Boolean(!value);
  }
  if (!value) {
    return !field.required;
  }
  return alternatives.some(a => a.value === value);
};

export const validateTreeChoiceValue = (field: MdfFieldEntity, treeAlternatives: string[][] | undefined, value: FieldValue): boolean => {
  if (field.type !== FieldType.TreeChoice) {
    return false;
  }

  if (!treeAlternatives && !value && !field.required) {
    return true; // Do not throw error if field is empty and not required and there is no alternatives.
  }

  if (!treeAlternatives) {
    return false;
  }
  if (!value) {
    return !field.required;
  }
  return treeAlternatives.some(el => isEqual(el, value));
};

export const validateMinMaxCharacters = (field: MdfFieldEntity, value: FieldValue): string | true => {
  if (field.type !== FieldType.Text && !field.freeform) {
    return true;
  }
  if (typeof field.maxCharacters !== 'number' && typeof field.minCharacters !== 'number') {
    return true;
  }
  if (typeof value !== 'string') {
    return true;
  }
  const length = [...value].length;
  if (length === 0) {
    return true; // 0 should not trigger this validation rule, but let the required validation rule take care of warning if the field must have a value.
  }
  if (typeof field.minCharacters === 'number' && length < field.minCharacters) {
    return `Not enough characters, ${length} of minimum ${field.minCharacters} characters required`;
  }
  if (typeof field.maxCharacters === 'number' && value.length > field.maxCharacters) {
    return `Too many characters ${length} of maximum ${field.maxCharacters}`;
  }

  return true;
};

/**
 * Generate a description based off a schema's rule
 * @param payload The MDF payload to fetch the values from
 * @param schema The schema the rule & fields are defined in
 */
export const generateDescription = (payload: ItemMetaData | TimedMetadata | null, schema: MdfSchemaNoViews): string | undefined => {
  if (!schema.rule) {
    return undefined;
  }

  // We first create the { fieldId -> fieldValueAsString }
  // mapping to use in the template replacement.
  const formValueReplacements: Record<string, string> = {};
  const idToFieldIds = fromPairs((schema.fields ?? []).map(field => [field.id, field.fieldId]));
  for (const [id, fieldValue] of Object.entries(payload?.formData ?? {})) {
    const fieldId = idToFieldIds[id];
    if (fieldId) {
      formValueReplacements[fieldId] = '' + fieldValue;
    }
  }

  const parser = new TemplateParser(schema.rule);
  return parser.replace(formValueReplacements);
};


/**
 * Validate whether incoming field value is defined, and has a valid value to satisfy
 * the required rule.
 *
 * @returns true if valid.
 */
export const validateRequired = (val: FieldValue | undefined | null): boolean => {
  if (typeof val === 'number' || typeof val === 'boolean') {
    return true;
  }
  if (!val) {
    return false;
  }
  const str = val.toString().trim();
  return str.length > 0;
};

/**
 * Creates a default LayoutSettings object for a given field.
 */
export const createDefaultSettings = (field: MdfFieldEntity, viewType: ViewType, mdfSource?: string): LayoutSettings => {

  let visible = true;

  // We want to show File types in upload dialog by default.
  if ((field.type === FieldType.File) && !(viewType === ViewType.UPLOAD)) {
    visible = false;
  }

  // We want to hide link types in the search view by default.
  if (field.type === FieldType.Link && viewType === ViewType.SEARCH) {
    visible = false;
  }

  if (field.type === FieldType.Button) {
    visible = isButtonFieldVisibleViewType(viewType);
  }

  return {
    id: field.id,
    fieldId: field.fieldId,
    icon: '',
    label: '',
    visible,
    hint: '',
    flex: 12,
    multiline: false,
    fieldHeight: 2,
    showAlways: true,
    mdfSource
  };
};

/**
 * Whether the given FieldType supports range search.
 */
export const supportsRange = (type: FieldType): boolean => {
  let rangeSupported = false;
  switch (type) {
    case FieldType.Number:
    case FieldType.Date:
      rangeSupported = true;
      break;
  }
  return rangeSupported;
};

/**
 * Whether a field has been saved. This is used to determine
 * whether a user is allowed to change a field type.
 */
export const isSavedField = (field: MdfFieldEntity | null): boolean => {
  if (field && field.id) {
    return !field.id.startsWith('fake_');
  }
  return false;
};

/**
 * Represents a MdfFieldEntity.
 */
export class MetaDataFieldImpl implements MdfFieldEntity {

  public id: string;
  public required: boolean = false;
  public systemDefaultField?: boolean;
  public alternatives?: Alternative[];
  public optionListId?: string;
  public initializationRules?: MdfFieldInitializationRule[];
  public freeform?: boolean;
  /**
   * Constructing a MdfFieldEntity will generate a fake id, that will be
   * replaced by a serverside generated id upon saving the first time.
   *
   * @param fieldId The initial field id value
   * @param type The initial field type
   * @param defaultValue optional default value
   */
  constructor(public fieldId: string, public type: FieldType, public defaultValue: FieldValue) {
    this.id = `fake_${createUuid()}`;
  }
}

/**
 * Checks if the given mdf payload is valid against the given model
 * by checking that:
 * - the mdf id is the same
 * - each field defined by the model is present in the payload
 * - for field types other than string, verify that the values are valid
 *
 * @param payload The payload to validate
 * @param model The model to validate against
 * @return null if the payload is valid or an error message otherwise
 */
export const isValid = (payload: ItemMetaData, model: MdfSchemaNoViews): string | null => {
  if (!payload || !model) {
    throw new Error('Internal error');
  }
  if (payload.formId !== model.id) {
    return 'Mismatching IDs';
  }

  if (!model.fields) {
    return null;
  }

  for (const field of model.fields) {
    const id = field.fieldId ? field.fieldId : field.id;

    if (!(field.fieldId in payload.formData) && !(field.id in payload.formData) && field.required === true) {
      return `Missing required field '${field.fieldId} in ${model.label}'`;
    }

    const value = payload.formData[field.id] ? payload.formData[field.id] : payload.formData[field.fieldId];
    if (!field.required && (typeof value !== 'boolean' && !value)) {
      continue;
    }

    if (field.type === FieldType.Text) {
      if ('string' !== typeof value) {
        return `Invalid text value "${JSON.stringify(value)}" for field ${id} in ${model.label}`;
      }
    } else if (field.type === FieldType.Checkbox) {
      if ('boolean' !== typeof value) {
        return `Invalid boolean value "${JSON.stringify(value)}" for field ${id} in ${model.label}`;
      }
    } else if (field.type === FieldType.Number) {
      if ('number' !== typeof value) {
        return `Invalid number value "${JSON.stringify(value)}" for field ${id} in ${model.label}`;
      }
    } else if (field.type === FieldType.Date) {
      if ('string' !== typeof value || isNaN(Date.parse(value))) {
        return `Invalid date value "${JSON.stringify(value)}" for field ${id} in ${model.label}`;
      }
    } else if (field.type === FieldType.Choice) {
      if (isUndefined(field.alternatives)) {
        return `The selected choice is not valid anymore for the field ${id} in ${model.label}`;
      }
      if ('string' !== typeof value || field.alternatives.filter(alternative => alternative.value === value).length === 0) {
        return `Invalid choice value for field ${id} in ${model.label}`;
      }
    }
  }

  return null;
};

/**
 * Given a payload, return a copy where the internal field id's have been replaced by user defineable fieldId
 *
 * Will also set default values for fields with no value, and a default value is configured in the schema for that field
 *
 * @param metadata The metadata to transform
 * @param mdfFields A flat list of field definitions of all used schemas
 */
export const replaceIdWithFieldIdForSchemasAndFillDefaultValues = (metadata: ItemMetaData, mdfFields: MdfFieldEntity[], shouldInjectDefaultValues: boolean = true): ItemMetaData => {
  const clone = cloneDeep(metadata);
  removeMdfNullValues(clone);

  const fieldByFieldId: Record<string, FieldValue> = {};
  for (const field of mdfFields) {
    const valueToAssign = getMdfValueForField(clone.formData[field.id], field, shouldInjectDefaultValues);
    if (valueToAssign !== undefined) {
      fieldByFieldId[field.fieldId] = valueToAssign;
    }
  }
  clone.formData = fieldByFieldId;
  return clone;
};

/**
 * Note: can return 'false' as return value (e.g., boolean fields)
 */
export function getMdfValueForField(valueFromMetadata: FieldValue | undefined, field: MdfFieldEntity, shouldInjectDefaultValues: boolean = true): FieldValue | undefined {

  const noValue = valueFromMetadata === undefined || (typeof valueFromMetadata === 'string' && valueFromMetadata.length === 0);
  if (noValue && shouldInjectDefaultValues && field.defaultValue !== undefined) {
    return field.defaultValue;
  } else if (!noValue) {
    return valueFromMetadata;
  }
  return undefined;
}

/**
 * Given a payload, return a copy where the user defined `fieldId`s and metadata form label are replaced by the internal GUID based `id`s
 *
 */
export const replaceUserDefinedIdsWithInternalId = <M extends MetadataType>(metadata: M, mainSchemaInternalId: string | null, fieldDefinitions: MdfFieldEntity[]): M => {
  const clone = cloneDeep(metadata);
  removeMdfNullValues(clone);

  const fieldsMap: Record<string, MdfFieldEntity> = keyBy(fieldDefinitions, (f) => f.fieldId);
  const fieldsById: Record<string, FieldValue | null> = {};
  for (const [key, value] of Object.entries(metadata.formData)) {
    const fieldDef = fieldsMap[key];
    if (fieldDef) {
      fieldsById[fieldDef.id] = value;
    } else {
      fieldsById[key] = value;
    }
  }

  clone.formId = mainSchemaInternalId ?? metadata.formId;
  clone.formData = fieldsById;
  return clone;
};



export const createEmptyMetadata = (): ItemMetaData => {
  return {
    formId: DEFAULT_MDF_ID,
    formData: {}
  };
};

export const getMdfFromFieldId = (fields: MdfFieldEntity[], fieldId: string) => {
  return fields?.find((field: MdfFieldEntity) => field.fieldId === fieldId);
};

export const getViewDetailsByFieldId = (views: Views, fieldId: string, viewType: ViewType) => {
  return views?.[viewType]?.find((view: LayoutSettings) => view.fieldId === fieldId);
};

type MetadataType = ItemMetaDataDto | MetadataDeltaPayload;

function doesMetadataHaveHighlightsOrDescription(metadata: Partial<MetadataType>): metadata is MetadataType & { highlights?: Record<string, string>, generated_description?: string } {
  return ('highlights' in metadata && typeof metadata.highlights === 'object')
    || ('generated_description' in metadata && typeof metadata.generated_description === 'string');
}

/**
 * Removes explicit null entries from formData, leaving only fields that have a value.
 */
export const omitNullFormDataFields = (formData: ItemMetaData['formData']): ItemMetaData['formData'] => omitBy(formData, isNil);

/**
 * Like omitNullFormDataFields, but also treats '' as unset - correct for Kelda's defaultMetadata and timedMetadata,
 * whose widgets can't represent null once rendered and round-trip unset fields to '' instead.
 */
export const omitUnsetFormDataFields = (formData: ItemMetaData['formData']): ItemMetaData['formData'] =>
  omitBy(formData, value => isNil(value) || value === '');

/**
 * Takes in an array of metadata objects and isIncrementalUpdate boolean value and returns a merged object.
 *
 * Uses lodash merge, so object preference is left to right i.e. subsequent props will
 * overwrite previous ones.
 *
 * Removes value from object if null is provided for a field and isIncrementalUpdate is set to true.
 */
export const getMergedMetadataObject = <M extends MetadataType>(metadataObjects: (Partial<M> | undefined)[], isIncrementalUpdate?: boolean): ItemMetaDataDto | TimedMetadataDto => {
  const defaultObject = createEmptyMetadata();
  let formId: string = DEFAULT_MDF_ID;
  const highlightObjects: Record<string, string>[] = [];
  let generatedDescription: string | undefined = undefined;
  const formDataObjects: Record<string, FieldValue | null>[] = [];
  for (const obj of metadataObjects) {
    if (!obj?.formData) {
      continue;
    }
    if (obj.formId) {
      formId = obj.formId;
    }
    if (doesMetadataHaveHighlightsOrDescription(obj)) {
      if (obj.highlights) {
        highlightObjects.push(obj.highlights);
      }
      if (obj.generated_description) {
        generatedDescription = obj.generated_description;
      }
    }
    formDataObjects.push(obj.formData);
  }

  const mergedFormData = mergeWith(
    defaultObject.formData, ...formDataObjects,
    (originalValue: FieldValue, newValue: FieldValue) => {
      if (typeof newValue === 'boolean' || isIncrementalUpdate) {
        return newValue;
      }
      if (newValue) {
        return newValue;
      }
      if (originalValue || typeof originalValue === 'boolean') {
        return originalValue;
      }
    }
  ) as Record<string, FieldValue>;
  const mergedHighlights = highlightObjects.length > 0 ? merge({}, ...highlightObjects) : undefined;

  // Typing all props as required to make sure each property is handled during merge
  const finalMetadata: Required<ItemMetaData> = {
    formId,
    formData: omitNullFormDataFields(mergedFormData),
    ...(mergedHighlights && { highlights: mergedHighlights }),
    ...(generatedDescription && { generated_description: generatedDescription }),
  };
  return finalMetadata;
};

export function getMdfDiffStrict(oldMetaData: ItemMetaData, newMetaData: ItemMetaData) {
  const mdfDiff: MdfDiffStrict = { oldMdfId: oldMetaData.formId, newMdfId: newMetaData.formId, metadataFields: [] };
  mdfDiff.metadataFields = getMdfFieldsDiff(oldMetaData, newMetaData);
  return mdfDiff;
}

export const getMdfDiff = (oldMetaData: ItemMetaData | undefined, newMetaData: Partial<ItemMetaData> | undefined) => {
  const mdfDiff: MdfDiff = { oldMdfId: oldMetaData?.formId, newMdfId: newMetaData?.formId, metadataFields: [] };
  mdfDiff.metadataFields = getMdfFieldsDiff(oldMetaData, newMetaData);
  return mdfDiff;
};

export const getMdfDeltaDiff = (oldMetaData: MetadataDeltaPayload | undefined, newMetaData: MetadataDeltaPayload | undefined) => {
  const mdfDiff: MdfDiffDelta = { oldMdfId: oldMetaData?.formId, newMdfId: newMetaData?.formId, metadataFields: [] };
  mdfDiff.metadataFields = getMdfFieldsDiffForMetadataDelta(oldMetaData, newMetaData);
  return mdfDiff;
};

function getMdfFieldsDiff(oldMetaData: ItemMetaData | undefined, newMetaData: Partial<ItemMetaData> | undefined) {
  let metadataFieldsWithDifferentValues: string[] = [];
  if (oldMetaData && newMetaData) {
    metadataFieldsWithDifferentValues = getPropertiesWithDifferentValues(oldMetaData.formData, newMetaData.formData);
  } else {
    const formData = newMetaData?.formData ?? oldMetaData?.formData;
    if (formData) {
      metadataFieldsWithDifferentValues = Object.keys(pickBy(formData, value => value !== ''));
    }
  }

  const fieldsDiff: FieldValueDiff[] = [];
  for (const field of metadataFieldsWithDifferentValues) {
    fieldsDiff.push({
      fieldId: field,
      oldValue: oldMetaData?.formData[field],
      newValue: newMetaData?.formData?.[field] });
  }
  return fieldsDiff;
}

function getMdfFieldsDiffForMetadataDelta(oldMetaData: MetadataDeltaPayload | undefined, newMetaData: Partial<MetadataDeltaPayload> | undefined) {
  let metadataFieldsWithDifferentValues: string[] = [];
  if (oldMetaData && newMetaData) {
    metadataFieldsWithDifferentValues = getPropertiesWithDifferentValuesIncludingNull(oldMetaData.formData, newMetaData.formData);
  } else {
    const formData = newMetaData?.formData ?? oldMetaData?.formData;
    if (formData) {
      metadataFieldsWithDifferentValues = Object.keys(pickBy(formData, value => value !== ''));
    }
  }

  const fieldsDiff: FieldValueDiffWithNull[] = [];
  for (const field of metadataFieldsWithDifferentValues) {
    fieldsDiff.push({
      fieldId: field,
      oldValue: oldMetaData?.formData[field],
      newValue: newMetaData?.formData?.[field] });
  }
  return fieldsDiff;
}

export const hasChangesInMetadata = (diff: MdfDiff) => {
  return diff.oldMdfId !== diff.newMdfId || diff.metadataFields.length > 0;
};

export type MdfSafeDiff = {safeFields: FieldValueDiff[], conflictedFields: FieldValueDiff[] };

export const addMissingFieldsToMdfSchema = (defaultForm: MdfEntity, mdfToUpdate: MdfEntity) => {
  if (!defaultForm.fields) {
    throw new Error('Expected fields to be present in default form!');
  }

  if (!mdfToUpdate.fields) {
    mdfToUpdate.fields = [];
  }

  const missingDefaultFields = defaultForm.fields
    .filter(f => !mdfToUpdate.fields?.find(mdfField => mdfField.id === f.id));

  if (missingDefaultFields.length > 0) {
    mdfToUpdate.fields.push(...missingDefaultFields);
  }
};

export const addMissingViewsToMdfSchema = (defaultForm: MdfEntity, mdfToUpdate: MdfEntity) => {
  if (!mdfToUpdate.views) {
    mdfToUpdate.views = {
      'item': [],
      'search': [],
      'editing': [],
      'upload': [],
      'share': [],
      'scheduler': [],
      'copyItem': [],
      'newVersion': [],
      'createPlaceholder': [],
      'folder': [],
    };
  }
  if (defaultForm.views) {
    const viewKeys = Object.values(ViewType);
    for (const type of viewKeys) {
      const defaultLayoutSettings = defaultForm.views[type];
      const existingLayoutSettings = mdfToUpdate.views[type];
      if (!defaultLayoutSettings || !existingLayoutSettings) {
        continue;
      }

      const missingDefaultViews = defaultLayoutSettings.filter(f => !existingLayoutSettings.find(mdfField => mdfField.id === f.id));

      if (missingDefaultViews.length > 0) {
        mdfToUpdate.views[type].push(...missingDefaultViews);
      }
    }
  } else {
    console.warn('Expected views to be present in default form!');
  }
};

/**
 * Apply configured default values to the metadata, if the value is unset or empty.
 * Note: assigns values to "id" as key
 */
export const fillDefaultValues = (metadata: ItemMetaData, fields: MdfFieldEntity[], removeDefaultCreatedOn: boolean) => {
  const fieldsWithDefaultValue = fields.filter(f => f.defaultValue !== undefined);

  for (const field of fieldsWithDefaultValue) {
    const valueToAssign = getMdfValueForField(metadata.formData[field.id], field);
    if (removeDefaultCreatedOn && field.id === String(SystemDefaultField.CreatedOn)) {
      continue;
    } else if (valueToAssign !== undefined) {
      metadata.formData[field.id] = valueToAssign;
    }
  }
  return metadata;
};

/**
 * Validates an item's metadata and removes the invalid fields from the payload
 * NOTE: Mutates the argument 'metadataPayload'
 * @param metadataPayload The metadata object passed in the payload
 * @param schema The schema to validate against
 */
export const validateAndSanitizeMdfFields = async (logger: Logger, fetchAlternatives: (field: MdfFieldEntity) => Promise<AlternativesType | undefined>, metadataPayload: ItemMetaData, fields?: MdfFieldEntity[]) => {
  if (!fields) {
    return;
  }

  const { formData } = metadataPayload;
  for (const field of fields) {
    let fieldValueOfId = formData[field.id];
    const alternatives = await fetchAlternatives(field);
    if (field.type === FieldType.Date) {
      const updatedValue = updateMetadataDateFieldsIfNeeded(metadataPayload.formData[field.id]!);
      if (updatedValue) {
        fieldValueOfId = updatedValue;
      }
    }
    const validationWithId = validateFieldValue(logger, fieldValueOfId, field.type, alternatives, field.freeform ?? false);

    if (validationWithId.isValid) {
      formData[field.id] = validationWithId.normalizedValue ?? fieldValueOfId;
      if (validationWithId.msg !== undefined) {
        logger.warn(validationWithId.msg);
      }
    } else {
      let fieldValueOfFieldId = formData[field.fieldId];
      if (field.type === FieldType.Date) {
        const updatedValue = updateMetadataDateFieldsIfNeeded(metadataPayload.formData[field.fieldId]!);
        if (updatedValue) {
          fieldValueOfFieldId = updatedValue;
        }
      }
      const validationWithFieldId = validateFieldValue(logger, fieldValueOfFieldId, field.type, alternatives, field.freeform ?? false);
      if (validationWithFieldId.isValid) {
        metadataPayload.formData[field.id] = validationWithId.normalizedValue ?? fieldValueOfFieldId;
      } else {
        delete metadataPayload.formData[field.id];
      }
    }

    // We should only remove this when the fieldId is not exactly the same as the id.
    // Otherwise SystemDefault fields would be stripped away.
    if (field.fieldId !== field.id) {
      delete metadataPayload.formData[field.fieldId];
    }
  }
};

export const getEmptyMetadataFieldPermissions = (): FieldPermissions => ({ read: {}, write: {} });

export async function validateAndFillDefaultValues(
  logger: Logger,
  fetchAlternatives: (field: MdfFieldEntity) => Promise<AlternativesType | undefined>,
  metadata: ItemMetaData,
  fields: MdfFieldEntity[],
  removeDefaultCreatedOn: boolean,
) {
  await validateAndSanitizeMdfFields(logger, fetchAlternatives, metadata, fields);
  fillDefaultValues(metadata, fields, removeDefaultCreatedOn);
}

const MAX_JSON_SIZE_IN_KB = 50;
export const validateJsonSize = (stringifiedValue: string) => {
  const estimatedSizeInKB = new TextEncoder().encode(stringifiedValue).length / 1024;
  const isSizeValid = estimatedSizeInKB <= MAX_JSON_SIZE_IN_KB;
  return {
    isValid: isSizeValid,
    msg: `Estimated size of given data (${Math.ceil(estimatedSizeInKB)} KB) ${isSizeValid ? 'meets requirements' : `exceeds maximum allowed size of ${MAX_JSON_SIZE_IN_KB} KB!`}`
  };
};

/**
 * Null values are considered empty for mdf fields, so we filter these out.
 */
export function removeMdfNullValues(metadata?: ItemMetaData | MetadataDeltaPayload) {
  if (metadata) {
    const strippedOfNull = pickBy(metadata.formData, value => value !== null);
    metadata.formData = strippedOfNull;
  }
}


export interface FieldValidationResult {
  /**
   * false if the input value cannot be accepted, even with some
   * normalization.
   */
  isValid: boolean;

  /**
   * Some optional human readable information explaining why the value
   * is rejected or normalized
   */
  msg?: string;

  /**
   * If defined, this is the adjusted value to be taken into account
   * instead of the orginal one.
   */
  normalizedValue?: any;
}


/**
 * Validates if a value conforms to a type of field
 * @param value The value to validate
 * @param fType The type to check for
 * @param alts The alternatives, if this field is a choice, multiple choice or treeChoice
 */
export const validateFieldValue = (logger: Logger, value: any, fType: FieldType, alternatives: AlternativesType = [], isFreeForm: boolean): { isValid: boolean, msg?: string, normalizedValue?: any } => {
  switch (fType) {
    case FieldType.File:
      return { isValid: isEmpty(value) };
    case FieldType.Link:
    case FieldType.Text:
      return { isValid: typeof value === 'string' };
    case FieldType.Number:
      return { isValid: typeof value === 'number' };
    case FieldType.Date:
      if (typeof value !== 'string') {
        return { isValid : false };
      }
      if (isIsoDateTime(value)) {
        return { isValid: true };
      } else {
        /**
         * TODO: For now, we are allowing users to store all flavors ISO_8601 format date time.
         * We should only support the formats supported by "convertDateTimetoIsoString",
         * rather than all flavors of ISO_8601.
         *
         * For more information: https://en.wikipedia.org/wiki/ISO_8601.
         *
         * The support should be removed after verifying that, our API users are using
         * the formats supported by "convertDateTimetoIsoString" function only.
         *
         * We need to update 'updateMetadataDateFieldsIfNeeded' function when the
         * date validation logic gets updated here.
         */
        logger.warn(`Provided Date time is in the wrong format. The date time: ${value}`);
        return { isValid: (typeof value === 'string' && moment(value, moment.ISO_8601, true).isValid()) };
      }
    case FieldType.Checkbox:
      return { isValid: typeof value === 'boolean' };
    case FieldType.Choice:
      return { isValid: (alternatives as Alternative[]).map(alt => alt.value).includes(value) };
    case FieldType.User:
      return { isValid: isValidGUID(value) };
    case FieldType.MultipleChoice: {
      const checkValidity = (mValue: string[]) => {
        if (isFreeForm) {
          if (mValue.every((val) => typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean')) {
            return { isValid: true };
          } else {
            return { isValid: false };
          }
        }
        for (const val of mValue) {
          const alternative = (alternatives as Alternative[]).find(alt => alt.value === val);
          if (!alternative) {
            return { isValid: false };
          }
        }
        return { isValid: true };
      };
      if (Array.isArray(value)) {
        return checkValidity(value);
      } else if (
        (typeof value === 'string' && value.length > 0)
        || typeof value === 'number'
        || typeof value === 'boolean'
      ) {
        const normalizedValue = [value.toString()];
        const result = checkValidity(normalizedValue);
        if (!result.isValid) {
          return result;
        }
        return {
          isValid: true,
          msg: `Multiple choice field value ${JSON.stringify(value)} has been normalized to ${JSON.stringify(normalizedValue)}`,
          normalizedValue,
        };
      } else {
        return { isValid: false };
      }
    }
    case FieldType.JSON: {
      try {
        const stringified = JSON.stringify(value);
        JSON.parse(stringified);
        return validateJsonSize(stringified);
      } catch (error: any) {
        return { isValid: false, msg: `Invalid JSON! ${exceptionEssence(error)}` };
      }
    }
    case FieldType.TreeChoice: {
      if (Array.isArray(value)) {
        if (isTreeChoiceAlternatives(alternatives)) {
          return { isValid: (alternatives as string[][]).some(el => isEqual(el, value)) };
        }
        return { isValid: true };
      }
      return { isValid: false };
    }
    default:
      return { isValid: true };
  }
};

export const updateMetadataDateFieldsIfNeeded = (dateString: FieldValue): FieldValue | undefined => {
  try {
    if (typeof dateString !== 'string') {
      return undefined;
    }
    if (isIsoDateTime(dateString)) {
      return dateString;
    } else {
      const convertedTime = convertDateTimetoIsoString(dateString);
      return convertedTime;
    }
  } catch (error: any) {
    return moment(dateString as string, moment.ISO_8601, true).isValid() ? dateString : undefined;
  }
};

/** This function casts parsed xml value according to mdf field type*/
export const castParsedFieldsValue = (parsedData: ParsedMetadata, fields: MdfFieldEntity[]): Record<string, FieldValue> => {
  const typedData: Record<string, FieldValue> = {};
  const fieldsMap: Record<string, MdfFieldEntity> = {};
  for (const field of fields) {
    fieldsMap[field.id] = field;
    fieldsMap[field.fieldId] = field;
  }
  for (const key in parsedData) {
    if (fieldsMap[key]) {
      let value = parsedData[key];
      const fieldType = fieldsMap[key].type;
      switch (fieldType) {
        case FieldType.File:
        case FieldType.Button:
          break;
        case FieldType.Link:
        case FieldType.Choice:
        case FieldType.SubType:
        case FieldType.Date:
          if (Array.isArray(value)) {
            value = value[0];
          }
          if (typeof value === 'string') {
            typedData[key] = value;
          }
          break;
        case FieldType.Text:
          if (Array.isArray(value)) {
            typedData[key] = value.join(' \n');
          } else if (typeof value === 'string') {
            typedData[key] = value;
          }
          break;
        case FieldType.Number:
          if (Array.isArray(value)) {
            value = value[0];
          }
          if (typeof Number(value) === 'number') {
            typedData[key] = Number(value);
          }
          break;
        case FieldType.Checkbox:
          if (Array.isArray(value)) {
            value = value[0];
          }
          if (typeof value === 'string' && value.toLowerCase() === 'true') {
            typedData[key] = true;
          } else if (typeof value === 'string' && value.toLowerCase() === 'false') {
            typedData[key] = false;
          }
          break;
        case FieldType.User:
          if (Array.isArray(value)) {
            value = value[0];
          }
          if (typeof value === 'string' && isValidGUID(value)) {
            typedData[key] = value;
          }
          break;
        case FieldType.TreeChoice:
        case FieldType.MultipleChoice:
          if (!Array.isArray(value)) {
            value = [value!];
          }
          typedData[key] = value;
          break;
        case FieldType.JSON: {
          try {
            if (Array.isArray(value)) {
              value = value[0];
            }
            value = JSON.parse(value!);
            typedData[key] = value!;
          } catch (error: any) {
            console.log(`Failed to parse json string - ${value}`);
          }
          break;
        }
        default:
          console.warn('Unhandled field type: ' + fieldType);
          typedData[key] = value!;
      }
    }
  }
  return typedData;
};

/**
 * Returns valid values. If values param contains an element which is not present in alternatives,
 * that element will be absent in return list,
 * @param values value to be saved in multi choice field.
 * @param alternatives alternatives present in schema.
 * @returns values that are also present in alternatives.
 */
export const extractValidMultipleChoiceValues = (values: string[], alternatives: Alternative[] | undefined) => {
  if (!alternatives || !alternatives.length) {
    return [];
  }
  return values.filter(val => !!alternatives.find(alt => alt.value === val));
};

export const getSystemDefaultCompactViewFields = () => {
  return { Common : ['0|title', '1|description'] };
};

function userCanViewOrModifyMdfType(canViewOrModifyGroups: string[], userGroups: string[]) {
  for (const group of userGroups) {
    if (canViewOrModifyGroups.find(g => g === group)) {
      return true;
    }
  }
  return false;
}

export function userCanViewMdfType(canViewGroups: string[], userGroups: string[]) {
  return userCanViewOrModifyMdfType(canViewGroups, userGroups);
}

export function userCanModifyMdfType(canModifyGroups: string[], userGroups: string[]) {
  return userCanViewOrModifyMdfType(canModifyGroups, userGroups);
}

export function doesSchemaSupportView(id: string, flavor: string | undefined, view: ViewType, isSubType: boolean | undefined) {
  // technical schemas only support item view
  if (flavor === TECHNICAL_DATA_FLAVOR) {
    return view === ViewType.ITEM;
  }
  // restricted material schema only supports item view
  if (id === RESTRICTED_MATERIAL_MDF_ID) {
    return view === ViewType.ITEM;
  }
  // detected-text-mdf schema only supports item view
  if (id ===  DETECTED_TEXT_MDF_ID) {
    return view === ViewType.ITEM;
  }
  // time-metadata schemas only support item and search view
  if (flavor === 'timed-metadata') {
    return view === ViewType.ITEM || view === ViewType.SEARCH;
  }

  if (view === ViewType.FOLDER) {
    return id === FOLDER_DEFAULT_MDF_ID || isSubType;
  }
  if (id === FOLDER_DEFAULT_MDF_ID) {
    return view === ViewType.SHARE || view === ViewType.SEARCH;
  }
  return true;
}

export function fieldTypeSupportInitializationRules(type: FieldType) {
  return type !== FieldType.User
  && type !== FieldType.Button
  && type !== FieldType.File;
}


function hasTitleInitializationRule(defaultSchema: MdfSchemaNoViews) {
  const rules = defaultSchema.fields?.find(f => f.id === SystemDefaultField.Title.toString())?.initializationRules;
  return rules !== undefined && rules.length > 0;
}

function hasDescriptionInitializationRule(defaultSchema: MdfSchemaNoViews) {
  const rules = defaultSchema.fields?.find(f => f.id === SystemDefaultField.Description.toString())?.initializationRules;
  return rules !== undefined && rules.length > 0;
}

/**
 * The legacy behaviour of ingesting file via S3 was to initialize the title and description
 * metadata fields with the file name. Since this is done before extracting exif data and
 * applying initialization rules, that meant that initialization rules for these fields
 * would never kick in, causing bugs like #11156.
 *
 * In order to fix the bug while retaining the existing behaviour for customers not
 * using initialization rules (so that we minimize the unforeseen side effects people
 * might rely on), this function applies the file name to these fields if and only if there
 * is no initialization rule or if the item is not an image, since exif data are currently
 * only extracted from images.
 */
export function applyLegacyInitialization(
  fileName: string,
  metadata: ItemMetaDataDto,
  defaultMdfSchema: MdfSchemaNoViews,
  itemType: DynamoItemType | undefined,
) {
  if ((itemType !== 'image' || !hasTitleInitializationRule(defaultMdfSchema!)) && !metadata.formData[SystemDefaultField.Title]) {
    metadata.formData[SystemDefaultField.Title] = fileName;
  }

  if ((itemType !== 'image' || !hasDescriptionInitializationRule(defaultMdfSchema!)) && !metadata.formData[SystemDefaultField.Description]) {
    metadata.formData[SystemDefaultField.Description] = fileName;
  }
}

export function getDateWithOffset(offset: number, defaultDate: FieldValue | undefined): string {
  const base = typeof defaultDate === 'string' && !isNaN(Date.parse(defaultDate))
    ? new Date(defaultDate)
    : new Date();
  base.setUTCDate(base.getUTCDate() + offset);
  return base.toISOString();
}

/**
 * Generates a tree structure for tree choice field.
 * @param data The treeAlternatives of MdfFieldEntity.
 * @returns Tree for tree choice field.
 */
export function generateTreeNodes(data: string[][]): TreeChoiceNode[] {
  const root: TreeChoiceNode[] = [];
  // Map to keep track of parent-child relationships
  const nodeMap: Record<string, TreeChoiceNode> = {};

  for (const path of data) {
    let parent: TreeChoiceNode | null = null;

    for (let i = 0; i < path.length; i++) {
      const label = safeArrayAccess(path, i);
      // Since same label can exist in different path, the nodeId should be unique.
      // The getNodePathToRoot returns the actual path to root.
      const nodeId: string = getNodePathToRoot(parent, label);

      if (!nodeMap[nodeId]) {
        const node: TreeChoiceNode = {
          label,
          children: [],
          parent,
        };

        nodeMap[nodeId] = node;

        if (parent) {
          parent.children.push(node);
        } else {
          root.push(node);
        }
      }

      parent = nodeMap[nodeId];
    }
  }
  return root;
}

function getNodePathToRoot(parentNode: TreeChoiceNode | null, label: string): string {
  let path: string = label;
  let currentNode = parentNode;

  while (currentNode) {
    path = currentNode.label + '/' + path;
    currentNode = currentNode.parent;
  }

  return path;
}
